import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/feature/MangaCard";
import JsonLd from "@/components/feature/JsonLd";
import { categories, mangaList } from "@/mocks/manga";

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("type") || "全部";
  const keyword = searchParams.get("keyword") || "";
  const [sort, setSort] = useState<"hot" | "rating" | "latest">("hot");

  const filtered = useMemo(() => {
    let list = mangaList;
    if (active !== "全部") {
      list = list.filter((m) => m.category === active);
    }
    if (keyword) {
      list = list.filter((m) => m.title.includes(keyword) || m.tags.some((t) => t.includes(keyword)));
    }
    if (sort === "hot") return [...list].sort((a, b) => b.likes - a.likes);
    if (sort === "rating") return [...list].sort((a, b) => b.rating - a.rating);
    return [...list].sort((a, b) => b.year - a.year);
  }, [active, keyword, sort]);

  const selectCategory = (cat: string) => {
    setSearchParams(cat === "全部" ? {} : { type: cat });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: active === "全部" ? "全部漫画" : `${active}漫画`,
            url: "https://mangains.github.io/category",
            description: `浏览${active === "全部" ? "全部" : active}漫画，支持漫画翻页模式、横屏阅读、夜间模式与缓存下载。`,
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", name: "漫画网", url: "https://mangains.github.io/" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://mangains.github.io/" },
              { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://mangains.github.io/category" },
            ],
          },
        ]}
      />
      <Navbar />

      <main className="container-site pt-28 md:pt-32 pb-16">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-foreground-600 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-950 font-medium">漫画分类</span>
        </nav>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
              {keyword ? `搜索：${keyword}` : active === "全部" ? "全部漫画" : `${active}漫画`}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              共 <strong className="text-primary-600">{filtered.length}</strong> 部漫画 · 支持漫画翻页模式、横屏阅读、夜间模式
            </p>
          </div>
        </div>

        {/* 分类切换 */}
        <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="漫画分类切换">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => selectCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                active === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* 排序 */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-foreground-600 mr-1">排序：</span>
          {[
            { key: "hot", label: "最热" },
            { key: "rating", label: "评分" },
            { key: "latest", label: "最新" },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key as typeof sort)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap ${
                sort === s.key
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 border border-background-200 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <i className="ri-search-eye-line text-5xl text-foreground-400"></i>
            <p className="mt-4 text-foreground-600">没有找到相关漫画，换个关键词试试吧</p>
            <Link to="/category" className="inline-block mt-5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors">
              查看全部漫画
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}